import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { HelmetProvider } from "react-helmet-async";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { lazy, Suspense, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { AuthProvider } from "@/contexts/AuthContext";
import ScrollRestoration from "./components/ScrollRestoration";
import CookieConsent from "./components/CookieConsent";
import { ServiceWorkerRegistration } from "./components/ServiceWorkerRegistration";
import AppInner from "./components/AppInner";
import GeoLocationProvider from "./components/GeoLocationProvider";
import Index from "./pages/Index";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Lazy load all non-homepage routes to reduce initial bundle
const About = lazy(() => import("./pages/About"));
const Collection = lazy(() => import("./pages/Collection"));
const NewArrivals = lazy(() => import("./pages/NewArrivals"));
const ProductDetail = lazy(() => import("./pages/ProductDetail"));
const Cart = lazy(() => import("./pages/Cart"));
const CheckoutSuccess = lazy(() => import("./pages/CheckoutSuccess"));
const Contact = lazy(() => import("./pages/Contact"));
const Profile = lazy(() => import("./pages/Profile"));
const Shipping = lazy(() => import("./pages/Shipping"));
const SizeGuide = lazy(() => import("./pages/SizeGuide"));
const FAQ = lazy(() => import("./pages/FAQ"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const TermsOfSale = lazy(() => import("./pages/TermsOfSale"));
const Lookbook = lazy(() => import("./pages/Lookbook"));
const Showcases = lazy(() => import("./pages/Showcases"));
const BlogArticle = lazy(() => import("./pages/BlogArticle"));
const NotFound = lazy(() => import("./pages/NotFound"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const TrackOrder = lazy(() => import("./pages/TrackOrder"));
const ReserveSuccess = lazy(() => import("./pages/ReserveSuccess"));

// Lazy load admin pages for code splitting
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminOrdersCombined = lazy(() => import("./pages/AdminOrdersCombined"));
const AdminProductsCombined = lazy(() => import("./pages/AdminProductsCombined"));
const AdminCustomersCombined = lazy(() => import("./pages/AdminCustomersCombined"));
const AdminMarketingCombined = lazy(() => import("./pages/AdminMarketingCombined"));
const AdminContentCombined = lazy(() => import("./pages/AdminContentCombined"));
const AdminAnalyticsCombined = lazy(() => import("./pages/AdminAnalyticsCombined"));
const AdminContacts = lazy(() => import("./pages/AdminContacts"));
const AdminSettings = lazy(() => import("./pages/AdminSettings"));

const queryClient = new QueryClient();

// Loading fallback for lazy-loaded pages
const PageLoadingFallback = () => (
  <div className="min-h-screen bg-background" />
);

const AdminLoadingFallback = () => (
  <div className="min-h-screen bg-background flex items-center justify-center">
    <div className="animate-pulse space-y-4 w-full max-w-md p-8">
      <div className="h-8 w-48 bg-muted rounded" />
      <div className="h-64 w-full bg-muted rounded" />
    </div>
  </div>
);

const App = () => {
  // Handle OAuth callback - ensure session is properly detected after redirect
  useEffect(() => {
    const handleOAuthRedirect = async () => {
      try {
        const url = new URL(window.location.href);

        const hashParams = new URLSearchParams(url.hash.replace(/^#/, ""));
        const accessToken = hashParams.get("access_token");
        const refreshToken = hashParams.get("refresh_token");

        if (accessToken && refreshToken) {
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (error) {
            console.warn("OAuth setSession failed", error);
          }
        }

        const code = url.searchParams.get("code");
        if (code) {
          const { error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            console.warn("OAuth code exchange failed", error);
          }
        }

        if (accessToken || refreshToken || code) {
          url.hash = "";
          url.searchParams.delete("code");
          url.searchParams.delete("state");
          const cleanPath = url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : "");
          window.history.replaceState(null, "", cleanPath);
        }
      } catch (e) {
        console.warn("OAuth redirect handling failed", e);
      }
    };

    void handleOAuthRedirect();
  }, []);

  return (
  <HelmetProvider>
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <ServiceWorkerRegistration />
      <AuthProvider>
      <GeoLocationProvider>
        <BrowserRouter>
          <ScrollRestoration />
          <AppInner>
        <ErrorBoundary>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/home" element={<Index />} />
          <Route path="/about" element={<Suspense fallback={<PageLoadingFallback />}><About /></Suspense>} />
          <Route path="/collection" element={<Suspense fallback={<PageLoadingFallback />}><Collection /></Suspense>} />
          <Route path="/new" element={<Suspense fallback={<PageLoadingFallback />}><NewArrivals /></Suspense>} />
          <Route path="/product/:productId" element={<Suspense fallback={<PageLoadingFallback />}><ProductDetail /></Suspense>} />
          <Route path="/cart" element={<Suspense fallback={<PageLoadingFallback />}><Cart /></Suspense>} />
          <Route path="/checkout/success" element={<Suspense fallback={<PageLoadingFallback />}><CheckoutSuccess /></Suspense>} />
          <Route path="/reserve/success" element={<Suspense fallback={<PageLoadingFallback />}><ReserveSuccess /></Suspense>} />
          <Route path="/contact" element={<Suspense fallback={<PageLoadingFallback />}><Contact /></Suspense>} />
          <Route path="/profile" element={<Suspense fallback={<PageLoadingFallback />}><Profile /></Suspense>} />
          <Route path="/track" element={<Suspense fallback={<PageLoadingFallback />}><TrackOrder /></Suspense>} />
          <Route path="/reset-password" element={<Suspense fallback={<PageLoadingFallback />}><ResetPassword /></Suspense>} />
          <Route path="/showcases" element={<Suspense fallback={<PageLoadingFallback />}><Showcases /></Suspense>} />
          {/* Combined admin routes */}
          <Route path="/admin" element={<Suspense fallback={<AdminLoadingFallback />}><AdminDashboard /></Suspense>} />
          <Route path="/admin/analytics" element={<Suspense fallback={<AdminLoadingFallback />}><AdminAnalyticsCombined /></Suspense>} />
          <Route path="/admin/orders" element={<Suspense fallback={<AdminLoadingFallback />}><AdminOrdersCombined /></Suspense>} />
          <Route path="/admin/products" element={<Suspense fallback={<AdminLoadingFallback />}><AdminProductsCombined /></Suspense>} />
          <Route path="/admin/customers" element={<Suspense fallback={<AdminLoadingFallback />}><AdminCustomersCombined /></Suspense>} />
          <Route path="/admin/marketing" element={<Suspense fallback={<AdminLoadingFallback />}><AdminMarketingCombined /></Suspense>} />
          <Route path="/admin/content" element={<Suspense fallback={<AdminLoadingFallback />}><AdminContentCombined /></Suspense>} />
          <Route path="/admin/contacts" element={<Suspense fallback={<AdminLoadingFallback />}><AdminContacts /></Suspense>} />
          <Route path="/admin/settings" element={<Suspense fallback={<AdminLoadingFallback />}><AdminSettings /></Suspense>} />
          <Route path="/blog/:slug" element={<Suspense fallback={<PageLoadingFallback />}><BlogArticle /></Suspense>} />
          <Route path="/shipping" element={<Suspense fallback={<PageLoadingFallback />}><Shipping /></Suspense>} />
          <Route path="/sizing" element={<Suspense fallback={<PageLoadingFallback />}><SizeGuide /></Suspense>} />
          <Route path="/faq" element={<Suspense fallback={<PageLoadingFallback />}><FAQ /></Suspense>} />
          <Route path="/privacy" element={<Suspense fallback={<PageLoadingFallback />}><PrivacyPolicy /></Suspense>} />
          <Route path="/terms" element={<Suspense fallback={<PageLoadingFallback />}><TermsOfService /></Suspense>} />
          <Route path="/terms-of-sale" element={<Suspense fallback={<PageLoadingFallback />}><TermsOfSale /></Suspense>} />
          <Route path="/lookbook" element={<Suspense fallback={<PageLoadingFallback />}><Lookbook /></Suspense>} />
          <Route path="*" element={<Suspense fallback={<PageLoadingFallback />}><NotFound /></Suspense>} />
        </Routes>
        </ErrorBoundary>
          </AppInner>
          <CookieConsent />
        </BrowserRouter>
      </GeoLocationProvider>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
  </HelmetProvider>
  );
};

export default App;
